/**
 * useDocumentLock — eDMS E1 (check-in / check-out lock).
 *
 * GET  /api/v1/documents/{id}/lock      → current lock state.
 * POST /api/v1/documents/{id}/checkout  → acquire the exclusive edit lock.
 * POST /api/v1/documents/{id}/checkin   → release it (owner, or force=admin).
 *
 * Served by the mock-api via the prod-proxy's 404-fallback (the real
 * document-svc has no /checkout path). Lock state is per-document.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface DocumentLock {
  locked_by_user_id: string;
  locked_by_name: string;
  locked_at_utc: string;
  note: string | null;
}
export interface LockState {
  document_id: string;
  locked: boolean;
  lock: DocumentLock | null;
}

export function useDocumentLockQuery(documentId: string | undefined): UseQueryResult<LockState> {
  return useQuery<LockState>({
    queryKey: ['documents', documentId, 'lock'],
    queryFn: ({ signal }) =>
      apiFetch<LockState>(`/api/v1/documents/${encodeURIComponent(documentId!)}/lock`, { signal }),
    enabled: !!documentId,
    staleTime: 10_000,
  });
}

export function useCheckoutMutation(documentId: string | undefined): UseMutationResult<LockState, Error, { note?: string }> {
  const qc = useQueryClient();
  return useMutation<LockState, Error, { note?: string }>({
    mutationFn: (vars) =>
      apiFetch<LockState>(`/api/v1/documents/${encodeURIComponent(documentId!)}/checkout`, {
        method: 'POST',
        body: { note: vars.note },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents', documentId, 'lock'] }),
  });
}

export function useCheckinMutation(documentId: string | undefined): UseMutationResult<LockState, Error, { force?: boolean }> {
  const qc = useQueryClient();
  return useMutation<LockState, Error, { force?: boolean }>({
    mutationFn: (vars) =>
      apiFetch<LockState>(`/api/v1/documents/${encodeURIComponent(documentId!)}/checkin`, {
        method: 'POST',
        body: { force: vars.force ?? false },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents', documentId, 'lock'] }),
  });
}
